/**
 * ConciergeChatPanel — the Concierge conversation surface.
 *
 * To stay visually coherent with the rest of the platform, this reuses the
 * SAME components and layout a normal agent chat uses:
 *   - the `.chat-messages-container` / `.chat-messages` scroll structure, so
 *     the scrollbar looks and sits exactly like a regular chat;
 *   - `MessageItem` for the bubbles (avatars, markdown, and the streaming
 *     "thinking" indicator — which shows while the assistant has no text yet,
 *     e.g. while it is running a tool to find an agent);
 *   - `ChatInput` for the typing bar (attachments disabled — the Concierge
 *     takes no files; the model picker uses the per-tenant Concierge config).
 * The one Concierge-specific element is the recommended `ConciergeHandoffCard`
 * ("Found your match!"), rendered inline in the timeline with a
 * one-shot `ConciergeCelebration` overlay when it first lands. Clicking the
 * card plays a short handoff beat, then opens a fresh, normal conversation
 * with that agent (`/chat?bot=<id>`), so access is re-enforced by the backend.
 *
 * The panel is CONTROLLED by the host: `conversationId` selects which session
 * is shown (the host owns the session list + the new/history controls). When
 * it changes, the effect keyed on `conversationId` reloads that session's
 * messages in place — hosts do NOT need to remount the panel via `key`
 * (ConciergePage deliberately doesn't).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { useLanguage } from '@/hooks/useLanguage';
import { useMetaAgentModel } from '@/hooks/useMetaAgentModel';
import { MessageItem } from '@modules/conversations/components/MessageItem';
import { ChatInput } from '@modules/conversations/components/ChatInput';
import { ConciergeHandoffCard } from './ConciergeHandoffCard';
import { ConciergeCelebration } from './ConciergeCelebration';
import { conciergeApi } from '../services/conciergeApi';
import { streamConciergeMessage } from '../services/conciergeStream';
import type { Message } from '@/types/models';
import type { ConciergeAgentCard } from '../types';
import './ConciergeChatPanel.css';
import { useSupportConversation } from '@/components/layout/supportConversation';

type TimelineItem =
  | { kind: 'user'; id: string; text: string; ts: string }
  | { kind: 'assistant'; id: string; text: string; ts: string; cutOff?: boolean; cutOffReason?: string }
  | { kind: 'card'; id: string; card: ConciergeAgentCard };

interface Props {
  /** The active Concierge conversation (owned by the page). */
  conversationId: string | null;
  /** Models the picker may offer ('all' or a whitelist). Tenant-level.
   *  Entries may be family keys or legacy concrete ids — ChatInput passes
   *  them to /ai/models/?allowed_models=, which normalizes both. */
  allowedModels?: string | string[];
  /** Model pre-selected in the picker. May be a family key ('haiku') or a
   *  legacy concrete id from an older tenant config — normalized to its
   *  family below so it matches the picker's family entries. */
  defaultModel?: string;
  /** Called after a turn completes so the page can refresh the history list. */
  onAfterTurn?: () => void;
  /** Called when the user picks a recommended agent (lets the page react). */
  onAgentChosen?: () => void;
  /**
   * Create the conversation on demand and return its id.
   *
   * The panel does NOT wait for a conversation to exist before letting someone
   * type — the row is created on the first send instead. That keeps the typing
   * bar usable the instant the page paints, and means a visit that never sends
   * anything leaves no empty conversation behind.
   */
  onEnsureConversation?: () => Promise<string | null>;
}

/** Treat an offset-less ISO timestamp as UTC rather than local. */
const _asUtc = (iso: string): string =>
  /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;

let _idSeq = 0;
const nextId = () => `c-${Date.now()}-${_idSeq++}`;

export function ConciergeChatPanel({
  conversationId,
  allowedModels = 'all',
  defaultModel = '',
  onAfterTurn,
  onAgentChosen,
  onEnsureConversation,
}: Props) {
  const { t } = useLanguage();
  useSupportConversation(conversationId);   // a support report sent from here names the Concierge
  const navigate = useNavigate();
  const tenantPath = useTenantPath();

  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [busy, setBusy] = useState(false);
  // The picker shows the person's pick, else the model the server runs this
  // conversation on, else the organization default; a turn carries a model
  // ONLY when it was picked here. Both reset on a conversation switch below.
  const [conversationModel, setConversationModel] = useState<string | null>(null);
  const { shownModel, requestedModel, pick: pickModel, clearPick } = useMetaAgentModel(
    conversationModel, defaultModel,
  );

  // --- Handoff celebration state ---------------------------------------
  // Which timeline card is currently celebrating (overlay mounted), and
  // which card the user clicked (drives the pre-navigation handoff beat).
  // Cards restored from a loaded conversation never celebrate: the history
  // hydration above only replays user/assistant messages ("ephemeral
  // agent-cards are not replayed"), so a 'card' entry can ONLY enter the
  // timeline via the live `agent_card` SSE event — and that is the single
  // place we arm the celebration.
  const [celebrateId, setCelebrateId] = useState<string | null>(null);
  const [departingId, setDepartingId] = useState<string | null>(null);

  // Smooth streaming typewriter — the SAME word-boundary animation the regular
  // chat uses (ConversationPanel's pushStreamText / tickStreamAnim), so a
  // Concierge reply streams exactly like every other agent's.
  //
  // This panel used to append each `ai_chunk` straight into the timeline, and
  // the *backend* faked the streaming feel by sleeping 15ms per word before
  // emitting it. That sleep was removed (it held a worker thread for ~1s per
  // reply to no purpose), which left the whole answer arriving in one burst.
  // The cadence belongs on the client anyway: the text is already complete
  // server-side, and animating here costs nothing but a rAF.
  const streamAnimRef = useRef<{
    target: string;     // full text received so far
    displayed: string;  // text currently on screen
    itemId: string;     // timeline id of the assistant turn being animated
    raf: number | null;
    lastTime: number;
  } | null>(null);

  const stopStreamAnim = useCallback(() => {
    const anim = streamAnimRef.current;
    if (anim && anim.raf !== null) cancelAnimationFrame(anim.raf);
    streamAnimRef.current = null;
  }, []);

  const tickStreamAnim = useCallback((now: number) => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    const elapsed = now - anim.lastTime;
    anim.lastTime = now;

    if (anim.target.length - anim.displayed.length <= 0) {
      anim.raf = null;  // caught up — idle until more text arrives
      return;
    }

    // Steady ~150 chars/sec, same rate as the regular chat. No catch-up
    // acceleration: racing ahead on a big buffer is what makes a reply
    // flash on screen and read as one-shot rather than streamed.
    const budget = Math.max(1, Math.round(0.15 * elapsed));
    let end = anim.displayed.length + budget;
    // Extend to the next word boundary so words never split mid-render.
    if (end < anim.target.length) {
      while (end < anim.target.length
             && anim.target[end] !== ' ' && anim.target[end] !== '\n') {
        end++;
      }
    }
    anim.displayed = anim.target.slice(0, Math.min(end, anim.target.length));

    const snapshot = anim.displayed;
    const id = anim.itemId;
    setTimeline(prev => prev.map(it =>
      it.kind === 'assistant' && it.id === id ? { ...it, text: snapshot } : it,
    ));
    anim.raf = requestAnimationFrame(tickStreamAnim);
  }, []);

  const startStreamAnim = useCallback((itemId: string) => {
    stopStreamAnim();
    streamAnimRef.current = {
      target: '', displayed: '', itemId, raf: null, lastTime: 0,
    };
  }, [stopStreamAnim]);

  const pushStreamText = useCallback((text: string) => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    anim.target = text;
    if (anim.raf === null) {
      anim.lastTime = performance.now();
      anim.raf = requestAnimationFrame(tickStreamAnim);
    }
  }, [tickStreamAnim]);

  /** Reveal everything still queued, immediately (turn finished / errored). */
  const flushStreamAnim = useCallback(() => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    if (anim.raf !== null) cancelAnimationFrame(anim.raf);
    anim.raf = null;
    if (anim.displayed !== anim.target) {
      const id = anim.itemId;
      const full = anim.target;
      setTimeline(prev => prev.map(it =>
        it.kind === 'assistant' && it.id === id ? { ...it, text: full } : it,
      ));
      anim.displayed = anim.target;
    }
  }, []);

  // Never leave a rAF running past unmount.
  useEffect(() => stopStreamAnim, [stopStreamAnim]);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Stick to the bottom only when the user is already there — same rule as the
  // regular chat, so reading scroll-back isn't yanked away by streaming text.
  const stickToBottomRef = useRef(true);

  // The conversation this panel created itself for an in-flight turn. Switching
  // to it must NOT reset the timeline: the user's message and the streaming
  // reply are already on screen, and the row is empty on the server until the
  // turn finishes. Resetting here is what made the first message of a session
  // look like it "restarted" and lose its answer.
  const selfCreatedRef = useRef<string | null>(null);

  // Load this conversation's prior messages on mount / when it changes. A
  // freshly-created (empty) session simply yields an empty timeline → greeting.
  useEffect(() => {
    let cancelled = false;
    if (conversationId && conversationId === selfCreatedRef.current) {
      selfCreatedRef.current = null;
      return;
    }
    // Switching conversations wipes the timeline the animator was writing
    // into — stop it rather than leave a rAF spinning against a dead id.
    stopStreamAnim();
    setTimeline([]);
    stickToBottomRef.current = true;
    // A pick belongs to its conversation; the self-created case above keeps it
    // (it was made for the conversation the first send just created).
    clearPick();
    setConversationModel(null);
    if (!conversationId) return;
    conciergeApi
      .getConversation(conversationId)
      .then(conv => {
        if (cancelled) return;
        setConversationModel(conv.effective_model ?? null);
        const msgs = conv.messages || [];
        const items: TimelineItem[] = [];
        for (const m of msgs) {
          if (m.sender_type === 'user') {
            items.push({ kind: 'user', id: m.message_id, text: m.message_content || '', ts: m.created_at });
          } else if (m.sender_type === 'assistant') {
            items.push({ kind: 'assistant', id: m.message_id, text: m.message_content || '', ts: m.created_at,
                         cutOff: m.message_metadata?.answer_cut_off === true,
                         cutOffReason: m.message_metadata?.answer_cut_off_reason });
          }
          // 'system' messages are not replayed.
        }
        // Replay the agent cards this conversation produced, each dropped BELOW
        // the reply that introduced it — the same place it sat when it was live.
        //
        // A card is emitted DURING its turn, so its timestamp is EARLIER than
        // the assistant message of that same turn (which is written when the
        // turn finishes). Anchoring on "the first message after the card" would
        // therefore put the card ABOVE the reply it belongs to. Anchor on the
        // first assistant reply at-or-after the card instead, and go under it.
        // Cards with no usable timestamp go to the end rather than vanish.
        // Cards arrive from the server in the order they were shown
        // (`_remember_card` appends). Two cards from the SAME turn anchor to
        // the SAME assistant message, and both computing `anchor + 1` would
        // splice the later one right on top of the earlier one — reversing
        // their live order on reload. Track how many cards this pass has
        // already placed at each anchor and stack after them, not on them.
        const cards = conv.agent_cards || [];
        const placedAtAnchor = new Map<number, number>();
        for (let i = 0; i < cards.length; i += 1) {
          const c = cards[i];
          // Older rows stored a naive timestamp; without an offset the browser
          // reads it as local time and the card sorts hours early — which is
          // what stacked every card at the top. Assume UTC when none is given.
          const at = c.shown_at ? Date.parse(_asUtc(c.shown_at)) : NaN;
          let insertAt = items.length;
          let anchor = -1;
          if (!Number.isNaN(at)) {
            anchor = items.findIndex(
              it => it.kind === 'assistant' && Date.parse(it.ts) >= at,
            );
            if (anchor >= 0) {
              const already = placedAtAnchor.get(anchor) ?? 0;
              insertAt = anchor + 1 + already;
            }
          }
          items.splice(insertAt, 0, {
            kind: 'card',
            id: `hist-card-${i}-${c.chatbot_id}`,
            card: c as ConciergeAgentCard,
          });
          if (anchor >= 0) {
            placedAtAnchor.set(anchor, (placedAtAnchor.get(anchor) ?? 0) + 1);
          }
        }
        setTimeline(items);
      })
      .catch(() => { /* a new/empty session — greeting stands */ });
    return () => { cancelled = true; };
  }, [conversationId, stopStreamAnim, clearPick]);

  // Auto-scroll to bottom on new content, but only if the user hasn't
  // scrolled up to read history. Track the LAST TEXT ITEM's length even when
  // a card sits at the end of the timeline — the assistant keeps streaming
  // text after the card is emitted, and without this the growing text above
  // the card would silently push it out of view.
  const lastText = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const it = timeline[i];
      if (it.kind !== 'card') return it.text.length;
    }
    return 0;
  }, [timeline]);
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [timeline.length, lastText]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const handleCardClick = useCallback(
    (chatbotId: string) => {
      onAgentChosen?.();
      navigate(tenantPath(`/chat?bot=${chatbotId}`));
    },
    [navigate, tenantPath, onAgentChosen],
  );

  const handleCelebrationDone = useCallback(() => setCelebrateId(null), []);

  const send = useCallback(
    async (raw: string) => {
      const text = (raw || '').trim();
      if (!text || busy) return;

      // Create the conversation on the first send rather than on mount.
      let cid = conversationId;
      if (!cid) {
        cid = (await onEnsureConversation?.()) ?? null;
        if (!cid) return;
        // Claim it before the id lands as a prop, so the reset effect leaves
        // this turn alone.
        selfCreatedRef.current = cid;
      }

      setBusy(true);
      stickToBottomRef.current = true;
      const ts = new Date().toISOString();
      const userId = nextId();
      const assistantId = nextId();
      setTimeline(prev => [
        ...prev,
        { kind: 'user', id: userId, text, ts },
        { kind: 'assistant', id: assistantId, text: '', ts },
      ]);
      startStreamAnim(assistantId);
      // Everything the server has sent for this turn. The animator reveals it
      // at a steady rate; this is the authoritative full text.
      let streamed = '';

      try {
        for await (const ev of streamConciergeMessage(cid, { message: text, model: requestedModel })) {
          if (ev.type === 'ai_chunk') {
            streamed += (ev as { content: string }).content || '';
            pushStreamText(streamed);
          } else if (ev.type === 'agent_card') {
            const card = ev as unknown as ConciergeAgentCard;
            const cardEntryId = nextId();
            setTimeline(prev => [...prev, { kind: 'card', id: cardEntryId, card }]);
            // Celebrate live-streamed cards (this handler is the only
            // producer of 'card' entries — history never replays them).
            setCelebrateId(cardEntryId);
          } else if (ev.type === 'ai_complete') {
            const msg = (ev as { message?: { message_content?: string;
                                             message_metadata?: { answer_cut_off?: boolean;
                                                                  answer_cut_off_reason?: string } } }).message;
            // The output cap paused it: the same note as every chat (see GuideChat).
            if (msg?.message_metadata?.answer_cut_off === true) {
              setTimeline(prev => prev.map(it => (
                it.kind === 'assistant' && it.id === assistantId
                  ? { ...it, cutOff: true, cutOffReason: msg.message_metadata?.answer_cut_off_reason } : it)));
            }
            const finalText = msg?.message_content;
            // The server's final copy is authoritative (it has been de-duped),
            // so it becomes the animation target rather than being slammed
            // straight into the timeline — otherwise the remaining animation
            // frames would fight it and the tail would jump.
            if (typeof finalText === 'string' && finalText) {
              streamed = finalText;
              pushStreamText(streamed);
            }
          } else if (ev.type === 'error') {
            flushStreamAnim();
            setTimeline(prev =>
              prev.map(it =>
                it.kind === 'assistant' && it.id === assistantId && !it.text
                  ? { ...it, text: t('concierge.error') || 'Sorry, something went wrong.' }
                  : it,
              ),
            );
          }
          // tool_debug / thinking_step events keep the assistant turn
          // text-less, which makes MessageItem show the regular "Thinking…"
          // indicator for as long as the turn is working. Deliberately NOT
          // the step-by-step banner: this is a user-facing surface, and the
          // shared banner renders tool names, ids and raw tool output.
        }
      } catch {
        flushStreamAnim();
        setTimeline(prev =>
          prev.map(it =>
            it.kind === 'assistant' && it.id === assistantId && !it.text
              ? { ...it, text: t('concierge.error') || 'Sorry, something went wrong.' }
              : it,
          ),
        );
      } finally {
        // Deliberately NOT flushing here. The animator runs on rAF,
        // independent of this loop, so it keeps revealing after the stream
        // closes — which is the whole point. Flushing would dump the tail
        // on screen the instant the server finished and undo the effect.
        setBusy(false);
        onAfterTurn?.();
      }
    },
    [conversationId, busy, requestedModel, t, onAfterTurn, onEnsureConversation,
     startStreamAnim, pushStreamText, flushStreamAnim],
  );

  const isEmpty = timeline.length === 0;
  // The id of the latest assistant turn — drives the streaming indicator.
  const lastAssistantId = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      if (timeline[i].kind === 'assistant') return timeline[i].id;
    }
    return undefined;
  }, [timeline]);

  return (
    <div className={`concierge-chat${departingId ? ' concierge-chat--handoff' : ''}`}>
      <div className="chat-messages-container concierge-messages" ref={scrollRef} onScroll={onScroll}>
        <div className="chat-messages">
          {isEmpty && (
            <div className="concierge-empty">
              <p className="concierge-empty-title">
                {t('concierge.greeting') || 'How can I point you in the right direction?'}
              </p>
              <p className="concierge-empty-sub">
                {t('concierge.greetingSub') ||
                  'Tell me what you’re trying to do and I’ll find the right agent for you.'}
              </p>
            </div>
          )}

          {timeline.map(item => {
            if (item.kind === 'card') {
              return (
                <div
                  key={item.id}
                  className={`concierge-card-row${departingId === item.id ? ' is-departing' : ''}`}
                  data-celebrate={celebrateId === item.id ? '' : undefined}
                >
                  <ConciergeHandoffCard
                    chatbotId={item.card.chatbot_id}
                    name={item.card.chatbot_name}
                    description={item.card.chatbot_description}
                    avatarColor={item.card.avatar_color}
                    avatarVariant={item.card.avatar_variant}
                    reason={item.card.reason}
                    onDepartStart={() => setDepartingId(item.id)}
                    onChoose={handleCardClick}
                  />
                </div>
              );
            }

            const message: Message = {
              message_id: item.id,
              conversation_id: conversationId || 'concierge',
              role: item.kind,
              content: item.text,
              created_at: item.ts,
              render_key: item.id,
              answer_cut_off: item.kind === 'assistant' && item.cutOff === true,
              answer_cut_off_reason: item.kind === 'assistant' ? item.cutOffReason : undefined,
            };
            // No botName/botColor → MessageItem falls back to the tenant brand
            // icon (the company logo) for the assistant avatar — what the
            // Concierge should wear.
            return (
              <MessageItem
                key={item.id}
                message={message}
                isStreaming={busy && item.id === lastAssistantId && !item.text}
              />
            );
          })}
        </div>
      </div>

      {celebrateId && (
        <ConciergeCelebration onDone={handleCelebrationDone} />
      )}

      <ChatInput
        onSendMessage={(content: string) => send(content)}
        selectedModel={shownModel}
        onModelChange={pickModel}
        allowedModels={allowedModels}
        // A real conversation with a real agent — asking the Concierge counts
        // as using the product, so the tour's steps complete here.
        journeyDetection
        disableAttachments
        sendDisabled={busy}
        isStreaming={busy}
      />
    </div>
  );
}
